import { defaultFormatValue } from '@/app/components/shared/forms/RangeSliderField'

import type { ActiveFilterChip, ListingFilterField, ListingRangeField } from './types'

/** Un campo select (`instantField` o cada `field`) está activo cuando tiene un
 * valor distinto de "sin filtrar" — cadena vacía o su `emptyValue` (p. ej.
 * `"todos"` para provincia, `"0"` para el precio mínimo "Cualquier precio"). */
function isFieldActive(field: ListingFilterField): boolean {
  const empty = field.emptyValue ?? ''
  return field.value !== '' && field.value !== empty
}

/** Texto visible del chip: el label de la opción seleccionada (no la clave
 * interna). Si no hubiera opción coincidente, cae al propio valor. */
function fieldChipLabel(field: ListingFilterField): string {
  return field.options.find((option) => option.value === field.value)?.label ?? field.value
}

/** El rango está activo cuando el usuario ha estrechado alguno de sus extremos
 * respecto a los límites completos (`min`/`max`) — si coincide con el rango
 * completo, equivale a "sin filtro". */
function isRangeActive(rangeField: ListingRangeField): boolean {
  return rangeField.value.min !== rangeField.min || rangeField.value.max !== rangeField.max
}

/** `[0, 0]` es el valor sentinela de "Mostrar solo gratuitos" — no un rango
 * real que el usuario pueda alcanzar arrastrando el slider (queda
 * deshabilitado mientras este filtro está activo). Compartido entre el chip
 * de "filtro aplicado" (aquí) y el texto del disparador del desplegable
 * (`RangeDropdownField`), para que ambos textos coincidan (bug detectado
 * 2026-09-11: sin este caso especial en el disparador, un rango `[0, 0]`
 * mostraba "0 €–0 €" en vez de anunciar el filtro de gratuitos). */
export function isFreeOnlyRangeValue(rangeField: Pick<ListingRangeField, 'value'>): boolean {
  return rangeField.value.min === 0 && rangeField.value.max === 0
}

function rangeChipLabel(rangeField: ListingRangeField): string {
  const format = rangeField.formatValue ?? defaultFormatValue
  // Caso "Mostrar solo gratuitos" (`[0, 0]`): un único texto en vez de "0 €–0 €".
  if (isFreeOnlyRangeValue(rangeField)) return 'Gratis'
  return `${format(rangeField.value.min)}–${format(rangeField.value.max)}`
}

/**
 * Deriva la lista de chips de "filtros aplicados" a partir de los campos que
 * `ListingFilters` ya recibe de cada página — sin conocer el dominio concreto
 * (provincia, categoría, precio…). La URL es la fuente de verdad: cada campo
 * expone su valor actual (leído de la URL por la página) y su `onChange`, así
 * que quitar un chip solo reusa ese `onChange` con el valor "vacío" del campo
 * (la página ya cableó si eso reescribe un query param o navega el segmento de
 * ruta de provincia).
 *
 * Excluye a propósito:
 * - `dateField`: la Fecha ya tiene su propia fila de chips removibles en
 *   `ListingFilters` (no se duplica).
 * - cualquier `field` con `chip: false` (p. ej. "Ordenar por": no es un filtro
 *   que se "quite").
 */
export function buildActiveFilterChips(args: {
  readonly instantField?: ListingFilterField
  readonly fields: readonly ListingFilterField[]
  readonly rangeField?: ListingRangeField
}): ActiveFilterChip[] {
  const { instantField, fields, rangeField } = args
  const chips: ActiveFilterChip[] = []

  const selectFields = instantField ? [instantField, ...fields] : fields
  for (const field of selectFields) {
    if (field.chip === false) continue
    if (!isFieldActive(field)) continue
    chips.push({
      id: field.label,
      label: fieldChipLabel(field),
      onRemove: () => field.onChange(field.emptyValue ?? ''),
    })
  }

  if (rangeField?.onClear && isRangeActive(rangeField)) {
    const onClear = rangeField.onClear
    chips.push({
      id: rangeField.label,
      label: rangeChipLabel(rangeField),
      onRemove: onClear,
    })
  }

  return chips
}
